import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  useTheme,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Paper,
  Stack,
  CircularProgress,
  Container,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItem,
  Pagination,
  Tooltip,
} from "@mui/material";
import { useQuery, useApolloClient } from "@apollo/client";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TagIcon from "@mui/icons-material/LocalOffer";
import InfoIcon from "@mui/icons-material/Info";
import FileValidationStatusChip from "../../components/FileValidationStatusChip/FileValidationStatusChip";
import BlinkingStatusDot from "../../components/BlinkingStatusDot/BlinkingStatusDot";
import FilterBar from "../../components/FilterNSearchBar/FilterNSearchBar";
import { GET_CONTROL_DETAIL_BY_ID } from "../../apis/settingsApis/framework-apis/frameworkApis";
import { getProcessedEvidenceByFileId } from "../../apis/settingsApis/processed-evidence/processedApis";
import { EvidenceStatus, CheckStatus } from "../../utils/enums";
import { renderCheckDetails } from "../Settings/Frameworks/FrameWorkDialogs/Control-Components/ControlComponents";
import InformationStepsCard from "../../components/InformationStepsCard/InformationStepsCard";

// Evidence List Item Component (same as ControlComponents)
const EvidenceListItem = ({
  fileDetail,
  index,
  currentPage,
  ITEMS_PER_PAGE,
  expandedEvidenceId,
  toggleExpand,
  evidenceChecksLoading,
  handleDownloadDocument,
  getFileValidationStatus,
  renderValidationStatusChip,
  renderStatusDot,
  renderCheckNumbers,
  renderCheckDetails,
}) => {
  const validationStatus = getFileValidationStatus(fileDetail);
  const isExpanded = expandedEvidenceId === fileDetail?.fileId;

  return (
    <React.Fragment>
      <ListItem
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #ddd",
          backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff",
          p: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: "#555",
            marginRight: 2,
            width: "20px",
          }}
        >
          {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}.
        </Typography>

        <Box
          sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          <Typography
            variant="body1"
            onClick={() => {
              handleDownloadDocument(fileDetail?.path, fileDetail?.maskName);
            }}
            sx={{
              fontWeight: 500,
              color: "#1976d2",
              cursor: "pointer",
            }}
          >
            {fileDetail?.maskName}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="caption" color="textSecondary">
              File Status: {validationStatus.status}
              {isExpanded && (
                <>
                  {" | Associated Checks: "}
                  {fileDetail?.associatedChecks?.length || 0}
                </>
              )}
              {isExpanded &&
                validationStatus.showChecks &&
                fileDetail?.associatedChecks?.length > 0 && (
                  <span style={{ marginLeft: "8px" }}>
                    IDs: [
                    {fileDetail.associatedChecks
                      .map((c) => c.checkId)
                      .join(", ")}
                    ]
                  </span>
                )}
            </Typography>
            {isExpanded &&
              renderCheckNumbers(
                fileDetail?.associatedChecks,
                validationStatus
              )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {renderValidationStatusChip(validationStatus)}
          {renderStatusDot(validationStatus)}

          {validationStatus.status === EvidenceStatus.VALID && (
            <IconButton
              size="small"
              onClick={() => toggleExpand(fileDetail?.fileId)}
              sx={{
                color: "primary.main",
                backgroundColor: "#f0f7ff",
                "&:hover": {
                  backgroundColor: "#e1f1ff",
                },
              }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
      </ListItem>

      {validationStatus.status !== EvidenceStatus?.INVALID &&
        (isExpanded && evidenceChecksLoading[fileDetail.fileId] ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 80,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            {renderCheckDetails(fileDetail?.associatedChecks, validationStatus)}
          </Collapse>
        ))}
    </React.Fragment>
  );
};

// Evidence Section Component (same as ControlComponents)
const EvidenceSection = ({
  totalItems,
  paginatedData,
  currentPage,
  totalPages,
  handlePageChange,
  expandedEvidenceId,
  toggleExpand,
  evidenceChecksLoading,
  handleDownloadDocument,
  getFileValidationStatus,
  renderValidationStatusChip,
  renderStatusDot,
  renderCheckNumbers,
  renderCheckDetails,
  ITEMS_PER_PAGE,
}) => {
  return (
    <Card
      sx={{
        borderRadius: 2,
        mb: 3,
      }}
      variant="outlined"
    >
      <CardContent sx={{ padding: "unset", paddingBottom: "unset !important" }}>
        {totalItems === 0 ? (
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ textAlign: "center", padding: "16px" }}
          >
            No Evidence Uploaded
          </Typography>
        ) : (
          <>
            <List sx={{ padding: 0, margin: 0 }}>
              {paginatedData?.map((fileDetail, index) => (
                <EvidenceListItem
                  key={fileDetail?.fileId}
                  fileDetail={fileDetail}
                  index={index}
                  currentPage={currentPage}
                  ITEMS_PER_PAGE={ITEMS_PER_PAGE}
                  expandedEvidenceId={expandedEvidenceId}
                  toggleExpand={toggleExpand}
                  evidenceChecksLoading={evidenceChecksLoading}
                  handleDownloadDocument={handleDownloadDocument}
                  getFileValidationStatus={getFileValidationStatus}
                  renderValidationStatusChip={renderValidationStatusChip}
                  renderStatusDot={renderStatusDot}
                  renderCheckNumbers={renderCheckNumbers}
                  renderCheckDetails={renderCheckDetails}
                />
              ))}
            </List>

            {/* Pagination Component */}
            {totalPages > 1 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                paddingTop={2}
                paddingBottom={2}
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const styles = {
  pageContainer: {
    minHeight: "100vh",
  },
  statsCard: {
    p: 2,
    borderRadius: "12px",
    border: "1px solid #E8EAED",
    backgroundColor: "#FFFFFF",
    flex: 1,
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      transform: "translateY(-2px)",
    },
  },
  mainCard: {
    borderRadius: "12px",
    border: "1px solid #E8EAED",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    overflow: "hidden",
  },
  cardHeader: {
    borderBottom: "1px solid #E8EAED",
    backgroundColor: "#F7F7FC",
    padding: "16px 24px",
    "& .MuiCardHeader-title": {
      fontSize: "18px",
      fontWeight: "600",
    },
  },
};

const EvidenceHome = () => {
  const [allEvidence, setAllEvidence] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("Valid");
  const [expandedEvidenceId, setExpandedEvidenceId] = useState(null);
  const [evidenceChecksData, setEvidenceChecksData] = useState({});
  const [evidenceChecksLoading, setEvidenceChecksLoading] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const theme = useTheme();
  const client = useApolloClient();

  const CONTROLS_ID = 131;
  const ITEMS_PER_PAGE = 5;

  const { data, loading, error } = useQuery(GET_CONTROL_DETAIL_BY_ID, {
    variables: { controls_id: CONTROLS_ID },
    errorPolicy: "All",
  });

  const fileDetails = useMemo(() => {
    return data?.getControlsDetailByControlId?.fileDetails || [];
  }, [data?.getControlsDetailByControlId?.fileDetails]);

  useEffect(() => {
    if (!fileDetails.length) return;

    // Filter out duplicates based on maskName using Set for O(n) time complexity
    const seenMaskNames = new Set();
    const uniqueFiles = fileDetails.filter((file) => {
      if (seenMaskNames.has(file.maskName)) {
        return false;
      }
      seenMaskNames.add(file.maskName);
      return true;
    });

    const processedEvidence = uniqueFiles.map((file) => ({
      ...file,
      validationStatus: file.isFileValid,
    }));
    setAllEvidence(processedEvidence);
  }, [fileDetails]);

  const getValidationStats = () => {
    const total = allEvidence.length;
    const valid = allEvidence.filter(
      (file) => file.isFileValid === true
    ).length;
    const invalid = allEvidence.filter(
      (file) => file.isFileValid === false
    ).length;
    const processing = allEvidence.filter(
      (file) => file.isFileValid === null
    ).length;
    return { total, valid, invalid, processing };
  };

  const getFilteredEvidence = () => {
    let filtered = allEvidence;
    if (statusFilter) {
      filtered = filtered.filter((file) => {
        switch (statusFilter) {
          case "Valid":
            return file.isFileValid === true;
          case "Invalid":
            return file.isFileValid === false;
          case "Processing":
            return file.isFileValid === null;
          default:
            return true;
        }
      });
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((file) => {
        return (
          file.maskName?.toLowerCase().includes(query) ||
          file.fileId?.toString().includes(query) ||
          file.path?.toLowerCase().includes(query)
        );
      });
    }
    return filtered;
  };

  const stats = getValidationStats();
  const filteredEvidence = getFilteredEvidence();
  const totalItems = filteredEvidence.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedData = filteredEvidence.slice(startIndex, endIndex);

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleFileDownload = (path, name) => {
    if (path) {
      const link = document.createElement("a");
      link.href = `${process.env.REACT_APP_CLOUDFRONT_URL}/${path}`;
      link.download = name || `evidence_${path}`;
      link.click();
    }
  };

  const checkNumberMap = useMemo(() => {
    const map = {};
    if (data?.getControlsDetailByControlId?.checks) {
      data.getControlsDetailByControlId.checks.forEach((check, index) => {
        const checkId = check.activeCheckId || check.checkId || check.id;
        if (checkId) {
          map[checkId] = index + 1;
          map[String(checkId)] = index + 1;
        }
      });
    }
    return map;
  }, [data?.getControlsDetailByControlId?.checks]);

  const getCheckStatusFromControl = (checkId) => {
    if (!data?.getControlsDetailByControlId?.checks) return CheckStatus.PENDING;
    const checkIdNum = Number(checkId);
    const checkIdStr = String(checkId);
    const controlCheck = data.getControlsDetailByControlId.checks.find(
      (check) => {
        const controlCheckId = check.activeCheckId || check.checkId || check.id;
        return (
          controlCheckId === checkId ||
          controlCheckId === checkIdNum ||
          controlCheckId === checkIdStr ||
          String(controlCheckId) === checkIdStr ||
          Number(controlCheckId) === checkIdNum
        );
      }
    );
    return controlCheck?.checkStatus || CheckStatus.PENDING;
  };

  const getFileValidationStatus = (fileDetail) => {
    const isFileValid = fileDetail.isFileValid;
    if (isFileValid === true) {
      return {
        status: EvidenceStatus?.VALID,
        message: "Valid",
        showChecks: true,
      };
    } else if (isFileValid === null) {
      return {
        status: EvidenceStatus?.PROCESSING,
        message: "Processing...",
        showChecks: false,
      };
    } else {
      return {
        status: EvidenceStatus?.INVALID,
        message: "Invalid",
        showChecks: false,
      };
    }
  };

  const renderValidationStatusChip = (validationStatus) => {
    return <FileValidationStatusChip validationStatus={validationStatus} />;
  };

  const renderStatusDot = (validationStatus) => {
    return <BlinkingStatusDot status={validationStatus.status} />;
  };

  const renderCheckNumbers = (associatedChecks, validationStatus) => {
    if (!validationStatus.showChecks || !associatedChecks?.length) return null;
    return (
      <Typography variant="caption" color="textSecondary">
        Check Numbers: [
        {associatedChecks
          .map((check) => checkNumberMap[check.checkId] || check.checkId)
          .join(", ")}
        ]
      </Typography>
    );
  };

  const toggleExpand = async (fileId) => {
    const isCurrentlyExpanded = expandedEvidenceId === fileId;
    if (isCurrentlyExpanded) {
      setExpandedEvidenceId(null);
      return;
    }
    setExpandedEvidenceId(fileId);
    if (evidenceChecksData[fileId] || evidenceChecksLoading[fileId]) return;
    setEvidenceChecksLoading((prev) => ({ ...prev, [fileId]: true }));
    try {
      const response = await client.query({
        query: getProcessedEvidenceByFileId,
        variables: { file_id: fileId },
        fetchPolicy: "network-only",
      });
      const evidenceData = response.data?.getProcessedEvidenceByFileId;
      let checks = [];
      if (Array.isArray(evidenceData)) {
        checks = evidenceData.flatMap((item) => item.checks || []);
      } else if (evidenceData) {
        checks = evidenceData.checks || [];
      }
      if (checks.length > 1) {
        const seen = new Set();
        checks = checks.filter((c) => {
          const key =
            c?.checkId != null
              ? String(c.checkId)
              : `${c?.checkName || ""}|${c?.checkType || ""}|${
                  c?.monitorType || ""
                }`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }
      setAllEvidence((prev) =>
        prev.map((file) =>
          file.fileId === fileId ? { ...file, associatedChecks: checks } : file
        )
      );
      setEvidenceChecksData((prev) => ({ ...prev, [fileId]: checks }));
    } catch (error) {
      console.error("Error fetching evidence checks:", error);
      setEvidenceChecksData((prev) => ({ ...prev, [fileId]: [] }));
    }
    setEvidenceChecksLoading((prev) => ({ ...prev, [fileId]: false }));
  };

  const categories = ["Valid", "Invalid", "Processing"];
  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
  };

  if (error) {
    return (
      <Box sx={styles.pageContainer}>
        <Container
          component="main"
          maxWidth="sm"
          sx={{ textAlign: "center", py: 8 }}
        >
          <ErrorIcon sx={{ fontSize: 64, color: "#EF4444", mb: 2 }} />
          <Typography variant="h4" color="#374151" gutterBottom>
            Unable to Load Evidence
          </Typography>
          <Typography variant="body1" color="#6B7280">
            Control ID {CONTROLS_ID} not found or you don't have access to view
            this evidence.
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={styles.pageContainer}>
      {/* Information Steps Card */}
      <Box sx={{ mb: 3 }}>
        <InformationStepsCard
          heading="**What should I do on this page?**"
          subtitle="This page displays all evidence files uploaded to demonstrate compliance with your selected frameworks."
          steps={[
            "View **evidence files** that have been uploaded to support compliance checks. These files help demonstrate that your organization meets framework requirements.",

            "Check the **validation status** of each file - **Valid** means the file is properly associated with checks, **Invalid** means there are issues, and **Processing** means the file is being analyzed.",

            "Use **filters** to quickly find files by status (Valid, Invalid, Processing) or search by file name, ID, or path.",

            "Click on a **Valid** file to expand and see which checks are associated with it. This helps you understand which compliance requirements are being met by the evidence.",

            "Download evidence files by clicking on the file name to access the original uploaded documents.",

            "Review the **statistics** at the top to get an overview of your evidence collection status across all files.",
          ]}
          footerText="💡 Ensure all evidence files are marked as Valid to maintain compliance. Review Invalid files to understand what needs to be fixed."
          collapsible={true}
          defaultExpanded={false}
        />
      </Box>

      <Box sx={{ mb: 1 }}>
        <Typography
          variant="h5"
          fontWeight="600"
          color={theme.customColors.text.strong}
          sx={{ mb: 0.5 }}
        >
          Evidence Files
        </Typography>
      </Box>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 4 }}>
        <Paper elevation={0} sx={styles.statsCard}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight="600"
                color={theme.customColors.text.strong}
              >
                {stats.total}
              </Typography>
              <Typography
                variant="body2"
                color={theme.customColors.text.secondary}
              >
                Total Evidence
              </Typography>
            </Box>
            <AssessmentIcon
              sx={{ color: theme.customColors.icons.primary, fontSize: 24 }}
            />
          </Box>
        </Paper>

        <Paper elevation={0} sx={styles.statsCard}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight="600"
                color={theme.customColors.status.success.text}
              >
                {stats.valid}
              </Typography>
              <Typography
                variant="body2"
                color={theme.customColors.text.secondary}
              >
                Valid Evidence
              </Typography>
            </Box>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: theme.customColors.status.success.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckCircleIcon
                sx={{ color: theme.customColors.button.text, fontSize: 16 }}
              />
            </Box>
          </Box>
        </Paper>

        <Paper elevation={0} sx={styles.statsCard}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight="600"
                color={theme.customColors.status.warning.text}
              >
                {stats.processing}
              </Typography>
              <Typography
                variant="body2"
                color={theme.customColors.text.secondary}
              >
                Processing Evidence
              </Typography>
            </Box>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: theme.customColors.status.warning.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HourglassEmptyIcon
                sx={{ color: theme.customColors.button.text, fontSize: 16 }}
              />
            </Box>
          </Box>
        </Paper>

        <Paper elevation={0} sx={styles.statsCard}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight="600"
                color={theme.customColors.status.error.text}
              >
                {stats.invalid}
              </Typography>
              <Typography
                variant="body2"
                color={theme.customColors.text.secondary}
              >
                Invalid Evidence
              </Typography>
            </Box>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: theme.customColors.status.error.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ErrorIcon
                sx={{ color: theme.customColors.button.text, fontSize: 16 }}
              />
            </Box>
          </Box>
        </Paper>
      </Stack>

      <Card sx={styles.mainCard} variant="outlined">
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="h5"
                fontWeight="600"
                color={theme.customColors.text.strong}
              >
                Evidence
              </Typography>
              {(searchQuery || statusFilter) && (
                <Chip
                  label="Filtered"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    height: 24,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: theme.customColors.text.secondary,
                    borderColor: theme.customColors.border.main,
                    background: theme.customColors.background.light,
                  }}
                />
              )}
            </Box>
          }
          sx={styles.cardHeader}
        />

        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 2,
              pb: 1.5,
              backgroundColor: theme.customColors.background.main,
            }}
          >
            <FilterBar
              searchQuery={searchQuery}
              onSearchChange={(e) => setSearchQuery(e.target.value)}
              filterLabel="Filter by Status"
              filters={categories}
              selectedFilter={statusFilter}
              onFilterChange={setStatusFilter}
              onClearFilters={clearFilters}
              countLabel={`${filteredEvidence.length} evidence${filteredEvidence.length !== 1 ? "s" : ""
                } found`}
            />
          </Box>

          <Divider sx={{ borderColor: theme.customColors.border.main }} />

          <Box>
            {loading ? (
              <Box sx={{ textAlign: "center", py: 12 }}>
                <CircularProgress
                  size={48}
                  thickness={4}
                  sx={{ color: "#3B82F6" }}
                />
                <Typography variant="body1" color="#6B7280" sx={{ mt: 2 }}>
                  Loading evidence files...
                </Typography>
              </Box>
            ) : (
              <EvidenceSection
                totalItems={totalItems}
                paginatedData={paginatedData}
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
                expandedEvidenceId={expandedEvidenceId}
                toggleExpand={toggleExpand}
                evidenceChecksLoading={evidenceChecksLoading}
                handleDownloadDocument={handleFileDownload}
                getFileValidationStatus={getFileValidationStatus}
                renderValidationStatusChip={renderValidationStatusChip}
                renderStatusDot={renderStatusDot}
                renderCheckNumbers={renderCheckNumbers}
                renderCheckDetails={(associatedChecks, validationStatus) =>
                  renderCheckDetails(
                    associatedChecks,
                    validationStatus,
                    checkNumberMap,
                    getCheckStatusFromControl
                  )
                }
                ITEMS_PER_PAGE={ITEMS_PER_PAGE}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EvidenceHome;
